import { ClientSearchParams } from 'src/app/shared/models/clients/clientSearchParams';
import { Document } from './../../../shared/models/documents/document';
import { Injectable } from '@angular/core';
import { ApiBaseService } from './api-base.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ErrorService } from '../error/error.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { SearchParams } from 'src/app/shared/models/searchParams';
import { Client } from 'src/app/shared/models/clients/client';
import { ClientHistorySearchResults } from 'src/app/shared/models/clients/clientHistorySearchResults';
import { ClientLiteSearchResults, ClientSearchResults } from 'src/app/shared/models/clients/clientSearchResults';
import { ClientHistory } from 'src/app/shared/models/clients/clientHistory';
import { ContactSearchResults } from 'src/app/shared/models/clients/contactSearchResults';
import { Contact } from 'src/app/shared/models/clients/contact';
import { BankDetail } from 'src/app/shared/models/clients/bankDetail';
import { BankSearchResults } from 'src/app/shared/models/clients/bankDetailSearchResults';
import { Note } from 'src/app/shared/models/clients/note';
import { PartnerSearchResults } from 'src/app/shared/models/clients/partnerSearchResults';
import { Partner } from 'src/app/shared/models/clients/partner';
import { ContractSearchResults } from 'src/app/shared/models/clients/contractSearchResults';
import { ClientContract } from 'src/app/shared/models/clients/clientContract';
import { ContactSearchParams } from 'src/app/shared/models/clients/contactSearchParams';
import { DocumentSearchResults } from 'src/app/shared/models/documents/documentSearchResults';

@Injectable({
    providedIn: 'root'
})
export class ClientsApiService extends ApiBaseService {

    baseUrl = environment.backend_host + '/api';

    constructor(
        public http: HttpClient,
        protected errorService: ErrorService,
        protected spinner: NgxSpinnerService) {
        super(spinner, errorService);
    }

    /***********************
    // CLIENTS
    /***********************/
    public getClients = (params: ClientSearchParams): Observable<ClientSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/clients/`, { params: p })
            .pipe(map(this.deserializeAs(ClientSearchResults)));
    }

    public getClientLite = (params: ClientSearchParams): Observable<ClientLiteSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/clients/lite`, { params: p })
            .pipe(map(this.deserializeAs(ClientLiteSearchResults)));
    }

    public getClientTiny = (params: SearchParams): Observable<ClientLiteSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/clients/tiny`, { params: p })
            .pipe(map(this.deserializeAs(ClientLiteSearchResults)));
    }

    public getClientSummary = (id: number): Observable<Client> => {
        return this.http.get<any>(`${this.baseUrl}/clients/${id}`)
            .pipe(map(this.deserializeAs(Client)));
    }

    public updateClient = (client: Client): Observable<Client> => {
        return this.http.put<any>(`${this.baseUrl}/clients/${client.id}`, this.serialize(client))
            .pipe(map(this.deserializeAs(Client)));
    }

    public createClient = (client: Client): Observable<Client> => {
        return this.http.post<any>(`${this.baseUrl}/clients/`, this.serialize(client))
            .pipe(map(this.deserializeAs(Client)));
    }

    public deleteClient = (client: Client): Observable<Client> => {
        return this.http.delete<any>(`${this.baseUrl}/clients/${client.id}`)
            .pipe(share());
    }

    public getClientHistory = (id: number): Observable<ClientHistorySearchResults> => {
        return this.http.get<any>(`${this.baseUrl}/clients/${id}/history`)
            .pipe(map(this.deserializeAs(ClientHistorySearchResults)));
    }

    public getClientHistoryDetail = (clientId: number, historyId: number): Observable<ClientHistory> => {
        return this.http.get<any>(`${this.baseUrl}/clients/${clientId}/history/${historyId}`)
            .pipe(map(this.deserializeAs(ClientHistory)));
    }

    public disconnectClient = (clientId: number, reason: string): Observable<any> => {
        return this.http.put<any>(`${this.baseUrl}/clients/${clientId}/disconnect`, { reason })
    }

    public connectClient = (clientId: number, reason: string): Observable<any> => {
        return this.http.put<any>(`${this.baseUrl}/clients/${clientId}/reconnect`, { reason });
    }

    public acceptNewClient = (id: number): Observable<any> => {
        return this.http.put<any>(`${this.baseUrl}/clients/${id}/accept`, {});
    }

    public rejectNewClient = (id: number, reason: string): Observable<any> => {
        return this.http.put<any>(`${this.baseUrl}/clients/${id}/reject`, { reason });
    }

    /***********************
    // CONTACTS
    /***********************/
    public getContacts = (params: ContactSearchParams): Observable<ContactSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/clients/contacts`, { params: p })
            .pipe(map(this.deserializeAs(ContactSearchResults)));
    }

    public getClientContacts = (params: SearchParams, id: number): Observable<ContactSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/clients/${id}/contacts`, { params: p })
            .pipe(map(this.deserializeAs(ContactSearchResults)));
    }

    public getClientContact = (clientId: number, contactId: number): Observable<Contact> => {
        return this.http.get<any>(`${this.baseUrl}/clients/${clientId}/contacts/${contactId}`)
            .pipe(map(this.deserializeAs(Contact)));
    }

    public createClientContact = (clientId: number, contact: Contact): Observable<Contact> => {
        return this.http.post<any>(`${this.baseUrl}/clients/${clientId}/contacts`, this.serialize(contact))
            .pipe(map(this.deserializeAs(Contact)));
    }

    public updateClientContact = (clientId: number, contact: Contact): Observable<Contact> => {
        return this.http.put<any>(`${this.baseUrl}/clients/${clientId}/contacts/${contact.id}`, this.serialize(contact))
            .pipe(map(this.deserializeAs(Contact)));
    }

    public deleteClientContact = (clientId: number, contactId: number): Observable<any> => {
        return this.http.delete<any>(`${this.baseUrl}/clients/${clientId}/contacts/${contactId}`)
            .pipe(share());
    }


    /***********************
    // BANK DETAILS
    /***********************/
    public getClientBanks = (params: SearchParams, id: number): Observable<BankSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/clients/${id}/bank-accounts`, { params: p })
            .pipe(map(this.deserializeAs(BankSearchResults)));
    }

    public getClientBank = (clientId: number, bankId: number): Observable<BankDetail> => {
        return this.http.get<any>(`${this.baseUrl}/clients/${clientId}/bank-accounts/${bankId}`)
            .pipe(map(this.deserializeAs(BankDetail)));
    }

    public createClientBank = (clientId: number, bank: BankDetail): Observable<BankDetail> => {
        return this.http.post<any>(`${this.baseUrl}/clients/${clientId}/bank-accounts`, this.serialize(bank))
            .pipe(map(this.deserializeAs(BankDetail)));
    }

    public updateClientBank = (clientId: number, bank: BankDetail): Observable<BankDetail> => {
        return this.http.put<any>(`${this.baseUrl}/clients/${clientId}/bank-accounts/${bank.id}`, this.serialize(bank))
            .pipe(map(this.deserializeAs(BankDetail)));
    }

    public deleteClientBank = (clientId: number, bankId: number): Observable<any> => {
        return this.http.delete<any>(`${this.baseUrl}/clients/${clientId}/bank-accounts/${bankId}`)
            .pipe(share());
    }


    /***********************
    // NOTES
    /***********************/
    public createClientNote = (clientId: number, note: Note): Observable<Note> => {
        return this.http.post<any>(`${this.baseUrl}/clients/${clientId}/notes`, this.serialize(note))
            .pipe(map(this.deserializeAs(Note)));
    }

    public updateClientNote = (clientId: number, note: Note): Observable<Note> => {
        return this.http.put<any>(`${this.baseUrl}/clients/${clientId}/notes/${note.id}`, this.serialize(note))
            .pipe(map(this.deserializeAs(Note)));
    }

    public deleteClientNote = (clientId: number, noteId: number): Observable<any> => {
        return this.http.delete<any>(`${this.baseUrl}/clients/${clientId}/notes/${noteId}`)
            .pipe(share());
    }


    /***********************
    // PARTNERS
    /***********************/
    public getClientPartners = (params: SearchParams, id: number): Observable<PartnerSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/clients/${id}/partners`, { params: p })
            .pipe(map(this.deserializeAs(PartnerSearchResults)));
    }

    public getClientPartner = (clientId: number, parnterId: number): Observable<Partner> => {
        return this.http.get<any>(`${this.baseUrl}/clients/${clientId}/partners/${parnterId}`)
            .pipe(map(this.deserializeAs(Partner)));
    }

    public createClientPartner = (clientId: number, partner: Partner): Observable<Partner> => {
        return this.http.post<any>(`${this.baseUrl}/clients/${clientId}/partners`, this.serialize(partner))
            .pipe(map(this.deserializeAs(Partner)));
    }

    public updateClientPartner = (clientId: number, partner: Partner): Observable<Partner> => {
        return this.http.put<any>(`${this.baseUrl}/clients/${clientId}/partners/${partner.id}`, this.serialize(partner))
            .pipe(map(this.deserializeAs(Partner)));
    }

    public deleteClientPartner = (clientId: number, parnterId: number): Observable<any> => {
        return this.http.delete<any>(`${this.baseUrl}/clients/${clientId}/partners/${parnterId}`)
            .pipe(share());
    }


    /***********************
    // CONTRACT
    /***********************/
    public getClientContracts = (params: SearchParams, id: number): Observable<ContractSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/clients/${id}/contracts`, { params: p })
            .pipe(map(this.deserializeAs(ContractSearchResults)));
    }

    public getClientContract = (clientId: number, contractId: number): Observable<ClientContract> => {
        return this.http.get<any>(`${this.baseUrl}/clients/${clientId}/contracts/${contractId}`)
            .pipe(map(this.deserializeAs(ClientContract)));
    }

    public createClientContract = (clientId: number, contract: ClientContract): Observable<ClientContract> => {
        return this.http.post<any>(`${this.baseUrl}/clients/${clientId}/contracts`, this.serialize(contract))
            .pipe(map(this.deserializeAs(ClientContract)));
    }

    public updateClientContract = (clientId: number, contract: ClientContract): Observable<ClientContract> => {
        return this.http.put<any>(`${this.baseUrl}/clients/${clientId}/contracts/${contract.id}`, this.serialize(contract))
            .pipe(map(this.deserializeAs(ClientContract)));
    }

    public deleteClientContract = (clientId: number, contractId: number): Observable<any> => {
        return this.http.delete<any>(`${this.baseUrl}/clients/${clientId}/contracts/${contractId}`)
            .pipe(share());
    }



    /***********************
    // Documents
    /***********************/

    public getClientDocuments = (id: number, params: SearchParams): Observable<DocumentSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/clients/${id}/documents`, { params: p })
            .pipe(map(this.deserializeAs(DocumentSearchResults)));
    }

    public uploadFileDocument = (id: number, formData: FormData): Observable<Document> =>
        this.http.post(`${this.baseUrl}/clients/${id}/documents`, formData)
            .pipe(map(this.deserializeAs(Document)));



}